import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/apiClient";
import { compressImageFileToDataUrl } from "@/lib/imageUpload";
import type {
  PitchCategoryMarketing,
  PitchCategoryMarketingSuggestionProfile,
  PitchCategoryMarketingSuggestions,
  PriceComparisonBasis,
  PriceComparisonReference,
} from "@/lib/onlineBooking";

type MarketingForm = {
  online_display_name: string;
  online_display_name_en: string;
  teaser_text: string;
  teaser_text_en: string;
  description_text: string;
  description_text_en: string;
  priority: string;
  online_visible: boolean;
  highlight_recommended: boolean;
  badge_text: string;
  badge_text_en: string;
  featuresText: string;
  featuresTextEn: string;
  hint_text: string;
  hint_text_en: string;
  upselling_enabled: boolean;
  price_comparison_reference: PriceComparisonReference;
  price_comparison_basis: PriceComparisonBasis;
  images: { sort_order: number; image_url: string; alt_text: string | null }[];
};

function toForm(data: PitchCategoryMarketing): MarketingForm {
  return {
    online_display_name: data.online_display_name ?? "",
    online_display_name_en: data.online_display_name_en ?? "",
    teaser_text: data.teaser_text ?? "",
    teaser_text_en: data.teaser_text_en ?? "",
    description_text: data.description_text ?? "",
    description_text_en: data.description_text_en ?? "",
    priority: String(data.priority ?? 0),
    online_visible: data.online_visible ?? true,
    highlight_recommended: data.highlight_recommended ?? false,
    badge_text: data.badge_text ?? "",
    badge_text_en: data.badge_text_en ?? "",
    featuresText: (data.features ?? []).join("\n"),
    featuresTextEn: (data.features_en ?? []).join("\n"),
    hint_text: data.hint_text ?? "",
    hint_text_en: data.hint_text_en ?? "",
    upselling_enabled: data.upselling_enabled ?? true,
    price_comparison_reference: data.price_comparison_reference ?? "recommended",
    price_comparison_basis: data.price_comparison_basis ?? "per_night",
    images: (data.images ?? []).map((img) => ({
      sort_order: img.sort_order,
      image_url: img.image_url,
      alt_text: img.alt_text,
    })),
  };
}

function LoadSuggestionButton({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={onClick}>
      Vorschlag laden
    </Button>
  );
}

function FieldLabel({
  children,
  action,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span>{children}</span>
      {action}
    </div>
  );
}

function applySuggestionProfile(
  profile: PitchCategoryMarketingSuggestionProfile,
): Partial<MarketingForm> {
  return {
    online_display_name: profile.online_display_name ?? "",
    online_display_name_en: profile.online_display_name_en ?? "",
    teaser_text: profile.teaser_text ?? "",
    teaser_text_en: profile.teaser_text_en ?? "",
    description_text: profile.description_text ?? "",
    description_text_en: profile.description_text_en ?? "",
    badge_text: profile.badge_text ?? "",
    badge_text_en: profile.badge_text_en ?? "",
    featuresText: (profile.features ?? []).join("\n"),
    featuresTextEn: (profile.features_en ?? []).join("\n"),
    hint_text: profile.hint_text ?? "",
    hint_text_en: profile.hint_text_en ?? "",
    highlight_recommended: profile.highlight_recommended,
    priority: String(profile.priority ?? 0),
  };
}

export interface CategoryMarketingSectionProps {
  categoryId: string;
  categoryName: string;
  canEdit: boolean;
  active: boolean;
  /** Wird vom Kategorie-Dialog mitgespeichert. */
  saveHandle?: { current: (() => Promise<void>) | null };
}

export function CategoryMarketingSection({
  categoryId,
  categoryName,
  canEdit,
  active,
  saveHandle,
}: CategoryMarketingSectionProps) {
  const [form, setForm] = useState<MarketingForm | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<PitchCategoryMarketingSuggestions | null>(null);
  const [selectedProfileId, setSelectedProfileId] = useState<string>("");

  const ensureSuggestions = useCallback(async () => {
    if (suggestions) return suggestions;
    const loaded = await api.get<PitchCategoryMarketingSuggestions>(
      `/api/v1/pitch-categories/${categoryId}/marketing/suggestions`,
    );
    setSuggestions(loaded);
    setSelectedProfileId((current) => current || loaded.recommended_profile_id);
    return loaded;
  }, [categoryId, suggestions]);

  const selectedProfile = suggestions?.profiles.find((p) => p.id === selectedProfileId);

  const loadFieldSuggestion = useCallback(
    async (apply: (profile: PitchCategoryMarketingSuggestionProfile) => Partial<MarketingForm>) => {
      if (!canEdit) return;
      try {
        const loaded = await ensureSuggestions();
        const profile =
          loaded.profiles.find((p) => p.id === selectedProfileId) ??
          loaded.profiles.find((p) => p.id === loaded.recommended_profile_id);
        if (!profile) return;
        setForm((f) => (f ? { ...f, ...apply(profile) } : f));
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Vorschläge konnten nicht geladen werden.");
      }
    },
    [canEdit, ensureSuggestions, selectedProfileId],
  );

  const applySelectedProfile = useCallback(async () => {
    if (!canEdit) return;
    try {
      const loaded = await ensureSuggestions();
      const profile =
        loaded.profiles.find((p) => p.id === selectedProfileId) ??
        loaded.profiles.find((p) => p.id === loaded.recommended_profile_id);
      if (!profile) return;
      setForm((f) => (f ? { ...f, ...applySuggestionProfile(profile) } : f));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Vorschläge konnten nicht geladen werden.");
    }
  }, [canEdit, ensureSuggestions, selectedProfileId]);

  useEffect(() => {
    if (!active || !categoryId) return;
    setLoading(true);
    setError(null);
    setSuggestions(null);
    setSelectedProfileId("");
    api
      .get<PitchCategoryMarketing>(`/api/v1/pitch-categories/${categoryId}/marketing`)
      .then((data) => setForm(toForm(data)))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [active, categoryId]);

  useEffect(() => {
    if (!active || !categoryId) return;
    void ensureSuggestions().catch(() => {
      /* optional – Fehler beim manuellen Laden */
    });
  }, [active, categoryId, ensureSuggestions]);

  async function onImageSelected(file: File | null) {
    if (!file || !form || !canEdit) return;
    if (!file.type.startsWith("image/")) {
      setError("Bitte wählen Sie eine Bilddatei (PNG, JPEG oder WebP).");
      return;
    }
    try {
      const imageData = await compressImageFileToDataUrl(file);
      setForm((f) =>
        f
          ? {
              ...f,
              images: [
                ...f.images,
                { sort_order: f.images.length, image_url: imageData, alt_text: categoryName },
              ],
            }
          : f,
      );
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bild konnte nicht geladen werden.");
    }
  }

  async function onSave() {
    if (!canEdit) return;
    if (!form) throw new Error("Online-Buchung wird noch geladen.");
    setSaving(true);
    setError(null);
    try {
      const features = form.featuresText
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
      const featuresEn = form.featuresTextEn
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
      await api.patch(`/api/v1/pitch-categories/${categoryId}/marketing`, {
        online_display_name: form.online_display_name.trim() || null,
        online_display_name_en: form.online_display_name_en.trim() || null,
        teaser_text: form.teaser_text.trim() || null,
        teaser_text_en: form.teaser_text_en.trim() || null,
        description_text: form.description_text.trim() || null,
        description_text_en: form.description_text_en.trim() || null,
        priority: Number(form.priority) || 0,
        online_visible: form.online_visible,
        highlight_recommended: form.highlight_recommended,
        badge_text: form.badge_text.trim() || null,
        badge_text_en: form.badge_text_en.trim() || null,
        features,
        features_en: featuresEn.length ? featuresEn : null,
        hint_text: form.hint_text.trim() || null,
        hint_text_en: form.hint_text_en.trim() || null,
        upselling_enabled: form.upselling_enabled,
        price_comparison_reference: form.price_comparison_reference,
        price_comparison_basis: form.price_comparison_basis,
        images: form.images,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Speichern fehlgeschlagen";
      setError(message);
      throw new Error(message);
    } finally {
      setSaving(false);
    }
  }

  const saveRef = useRef(onSave);
  saveRef.current = onSave;
  useEffect(() => {
    if (!saveHandle) return;
    saveHandle.current = () => saveRef.current();
    return () => {
      saveHandle.current = null;
    };
  }, [saveHandle]);

  if (loading || !form) {
    return <p className="text-sm text-label-secondary">Online-Marketing wird geladen…</p>;
  }

  const previewName = form.online_display_name.trim() || categoryName;

  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
        Online-Buchung
      </h3>
      <div className="space-y-4 rounded-xl border border-separator bg-fill-tertiary/30 p-4">
      <button
        type="button"
        role="switch"
        aria-checked={form.online_visible}
        disabled={!canEdit}
        onClick={() =>
          setForm((current) => (current ? { ...current, online_visible: !current.online_visible } : current))
        }
        className="flex w-full items-center justify-between gap-4 rounded-xl border border-separator bg-card px-4 py-3 text-left disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className="min-w-0">
          <span className="block text-sm font-medium text-label">In der Online-Buchung anzeigen</span>
          <span className="mt-0.5 block text-xs text-label-secondary">
            {form.online_visible
              ? "Gäste sehen diese Kategorie in der Online-Buchung. Texte, Bilder und Upselling können Sie darunter pflegen."
              : "Gäste sehen diese Kategorie nicht in der Online-Buchung. Gespeicherte Texte bleiben erhalten."}
          </span>
        </span>
        <span
          className={
            form.online_visible
              ? "shrink-0 rounded-full bg-status-green/15 px-3 py-1 text-sm font-semibold text-status-green"
              : "shrink-0 rounded-full bg-fill-tertiary px-3 py-1 text-sm font-semibold text-label-secondary"
          }
        >
          {form.online_visible ? "Sichtbar" : "Verborgen"}
        </span>
      </button>

      {form.online_visible ? (
      <>
      <p className="text-xs text-label-secondary">
        Werbetexte, Bilder und Priorität für die Kategorieauswahl. Niedrigere Prioritätszahl =
        höhere Bevorzugung bei der Vorauswahl.
      </p>

      {canEdit ? (
        <div className="rounded-xl border border-separator bg-fill-quaternary p-4 space-y-3">
          <div>
            <p className="text-sm font-medium">Textvorschläge</p>
            <p className="mt-1 text-xs text-label-secondary">
              Serverseitige Beispieltexte als Ausgangspunkt – nach dem Übernehmen bitte anpassen
              und speichern.
              {suggestions?.recommended_profile_id ? (
                <>
                  {" "}
                  Empfohlen für „{categoryName}“:{" "}
                  <span className="font-medium">
                    {suggestions.profiles.find((p) => p.id === suggestions.recommended_profile_id)
                      ?.label ?? suggestions.recommended_profile_id}
                  </span>
                  .
                </>
              ) : null}
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex min-w-[12rem] flex-col gap-1 text-sm">
              Vorlagenprofil
              <select
                className="eh-input"
                value={selectedProfileId || suggestions?.recommended_profile_id || ""}
                onChange={(e) => setSelectedProfileId(e.target.value)}
                disabled={!suggestions}
              >
                {(suggestions?.profiles ?? []).map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.label}
                  </option>
                ))}
              </select>
            </label>
            <Button
              type="button"
              variant="secondary"
              disabled={!suggestions}
              onClick={() => void applySelectedProfile()}
            >
              Vorschlag übernehmen
            </Button>
          </div>
          {selectedProfile?.teaser_text ? (
            <p className="text-xs text-label-tertiary italic">
              Vorschau: {selectedProfile.teaser_text}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <label className="flex flex-col gap-1 text-sm">
            <FieldLabel
              action={
                <LoadSuggestionButton
                  disabled={!canEdit}
                  onClick={() =>
                    void loadFieldSuggestion((profile) => ({
                      online_display_name: profile.online_display_name ?? "",
                      online_display_name_en: profile.online_display_name_en ?? "",
                    }))
                  }
                />
              }
            >
              Online-Anzeigename (Deutsch)
            </FieldLabel>
            <input
              disabled={!canEdit}
              className="eh-input"
              value={form.online_display_name}
              onChange={(e) => setForm((f) => (f ? { ...f, online_display_name: e.target.value } : f))}
              placeholder={categoryName}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Online-Anzeigename (Englisch)
            <input
              disabled={!canEdit}
              className="eh-input"
              value={form.online_display_name_en}
              onChange={(e) =>
                setForm((f) => (f ? { ...f, online_display_name_en: e.target.value } : f))
              }
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <FieldLabel
              action={
                <LoadSuggestionButton
                  disabled={!canEdit}
                  onClick={() =>
                    void loadFieldSuggestion((profile) => ({
                      teaser_text: profile.teaser_text ?? "",
                      teaser_text_en: profile.teaser_text_en ?? "",
                    }))
                  }
                />
              }
            >
              Kurzer Werbetext / Teaser (Deutsch)
            </FieldLabel>
            <textarea
              disabled={!canEdit}
              className="eh-input min-h-[4rem]"
              value={form.teaser_text}
              onChange={(e) => setForm((f) => (f ? { ...f, teaser_text: e.target.value } : f))}
              placeholder="z. B. Mehr Komfort und beste Lage"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Kurzer Werbetext / Teaser (Englisch)
            <textarea
              disabled={!canEdit}
              className="eh-input min-h-[4rem]"
              value={form.teaser_text_en}
              onChange={(e) => setForm((f) => (f ? { ...f, teaser_text_en: e.target.value } : f))}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <FieldLabel
              action={
                <LoadSuggestionButton
                  disabled={!canEdit}
                  onClick={() =>
                    void loadFieldSuggestion((profile) => ({
                      description_text: profile.description_text ?? "",
                      description_text_en: profile.description_text_en ?? "",
                    }))
                  }
                />
              }
            >
              Ausführliche Beschreibung (Deutsch)
            </FieldLabel>
            <textarea
              disabled={!canEdit}
              className="eh-input min-h-[5rem]"
              value={form.description_text}
              onChange={(e) => setForm((f) => (f ? { ...f, description_text: e.target.value } : f))}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Ausführliche Beschreibung (Englisch)
            <textarea
              disabled={!canEdit}
              className="eh-input min-h-[5rem]"
              value={form.description_text_en}
              onChange={(e) =>
                setForm((f) => (f ? { ...f, description_text_en: e.target.value } : f))
              }
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <FieldLabel
              action={
                <LoadSuggestionButton
                  disabled={!canEdit}
                  onClick={() =>
                    void loadFieldSuggestion((profile) => ({
                      featuresText: (profile.features ?? []).join("\n"),
                      featuresTextEn: (profile.features_en ?? []).join("\n"),
                    }))
                  }
                />
              }
            >
              Vorteile (Deutsch, eine Zeile pro Punkt)
            </FieldLabel>
            <textarea
              disabled={!canEdit}
              className="eh-input min-h-[5rem] font-mono text-xs"
              value={form.featuresText}
              onChange={(e) => setForm((f) => (f ? { ...f, featuresText: e.target.value } : f))}
              placeholder={"Stromanschluss\nGroße Stellfläche"}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Vorteile (Englisch, eine Zeile pro Punkt)
            <textarea
              disabled={!canEdit}
              className="eh-input min-h-[5rem] font-mono text-xs"
              value={form.featuresTextEn}
              onChange={(e) => setForm((f) => (f ? { ...f, featuresTextEn: e.target.value } : f))}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <FieldLabel
              action={
                <LoadSuggestionButton
                  disabled={!canEdit}
                  onClick={() =>
                    void loadFieldSuggestion((profile) => ({
                      badge_text: profile.badge_text ?? "",
                      badge_text_en: profile.badge_text_en ?? "",
                    }))
                  }
                />
              }
            >
              Badge-Text (Deutsch)
            </FieldLabel>
            <input
              disabled={!canEdit}
              className="eh-input"
              value={form.badge_text}
              onChange={(e) => setForm((f) => (f ? { ...f, badge_text: e.target.value } : f))}
              placeholder="z. B. Beliebt, Empfohlen"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Badge-Text (Englisch)
            <input
              disabled={!canEdit}
              className="eh-input"
              value={form.badge_text_en}
              onChange={(e) => setForm((f) => (f ? { ...f, badge_text_en: e.target.value } : f))}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <FieldLabel
              action={
                <LoadSuggestionButton
                  disabled={!canEdit}
                  onClick={() =>
                    void loadFieldSuggestion((profile) => ({
                      hint_text: profile.hint_text ?? "",
                      hint_text_en: profile.hint_text_en ?? "",
                    }))
                  }
                />
              }
            >
              Hinweistext (Deutsch)
            </FieldLabel>
            <input
              disabled={!canEdit}
              className="eh-input"
              value={form.hint_text}
              onChange={(e) => setForm((f) => (f ? { ...f, hint_text: e.target.value } : f))}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Hinweistext (Englisch)
            <input
              disabled={!canEdit}
              className="eh-input"
              value={form.hint_text_en}
              onChange={(e) => setForm((f) => (f ? { ...f, hint_text_en: e.target.value } : f))}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Priorität (Sortierung / Vorauswahl)
            <input
              type="number"
              disabled={!canEdit}
              className="eh-input w-28"
              value={form.priority}
              onChange={(e) => setForm((f) => (f ? { ...f, priority: e.target.value } : f))}
            />
          </label>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={form.highlight_recommended}
                onChange={(e) =>
                  setForm((f) => (f ? { ...f, highlight_recommended: e.target.checked } : f))
                }
              />
              Empfehlung hervorheben
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={form.upselling_enabled}
                onChange={(e) =>
                  setForm((f) => (f ? { ...f, upselling_enabled: e.target.checked } : f))
                }
              />
              Upselling aktiv
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              Preisvergleich – Bezug
              <select
                disabled={!canEdit}
                className="eh-input"
                value={form.price_comparison_reference}
                onChange={(e) =>
                  setForm((f) =>
                    f
                      ? {
                          ...f,
                          price_comparison_reference: e.target
                            .value as PriceComparisonReference,
                        }
                      : f,
                  )
                }
              >
                <option value="recommended">Empfohlene Kategorie</option>
                <option value="most_expensive">Teuerste Kategorie</option>
                <option value="selected">Aktuell gewählte Kategorie</option>
                <option value="hide">Ausblenden</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Preisvergleich – Darstellung
              <select
                disabled={!canEdit}
                className="eh-input"
                value={form.price_comparison_basis}
                onChange={(e) =>
                  setForm((f) =>
                    f
                      ? { ...f, price_comparison_basis: e.target.value as PriceComparisonBasis }
                      : f,
                  )
                }
              >
                <option value="per_night">Differenz pro Nacht</option>
                <option value="total_stay">Gesamtaufenthalt</option>
                <option value="hide">Ausblenden</option>
              </select>
            </label>
          </div>
          {canEdit ? (
            <div>
              <p className="mb-2 text-sm font-medium">Bilder</p>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => void onImageSelected(e.target.files?.[0] ?? null)}
              />
              <p className="mt-1 text-xs text-label-secondary">
                Große Fotos werden beim Hochladen automatisch verkleinert (max. ca. 2 MB
                Datengröße).
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {form.images.map((img, index) => (
                  <div key={`${index}-${img.image_url.slice(0, 24)}`} className="relative">
                    <img
                      src={img.image_url}
                      alt={img.alt_text ?? ""}
                      className="h-20 w-28 rounded-lg object-cover border border-separator"
                    />
                    <button
                      type="button"
                      className="absolute -right-1 -top-1 rounded-full bg-status-red px-1.5 text-xs text-white"
                      onClick={() =>
                        setForm((f) =>
                          f
                            ? {
                                ...f,
                                images: f.images.filter((_, i) => i !== index),
                              }
                            : f,
                        )
                      }
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="rounded-xl border border-separator bg-surface-secondary p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-label-secondary">
            Vorschau Online-Buchung
          </p>
          <article className="overflow-hidden rounded-xl border border-separator bg-surface-primary shadow-sm">
            {form.images[0] ? (
              <img
                src={form.images[0].image_url}
                alt=""
                className="aspect-[16/10] w-full object-cover"
              />
            ) : (
              <div className="flex aspect-[16/10] items-center justify-center bg-surface-tertiary text-2xl font-semibold text-label-tertiary">
                {previewName.charAt(0)}
              </div>
            )}
            <div className="space-y-2 p-4">
              {form.badge_text ? (
                <span className="inline-block rounded-full bg-brand-600 px-2 py-0.5 text-xs text-white">
                  {form.badge_text}
                </span>
              ) : null}
              <h4 className="font-semibold">{previewName}</h4>
              {form.teaser_text ? (
                <p className="text-sm text-label-secondary">{form.teaser_text}</p>
              ) : null}
              {form.featuresText ? (
                <ul className="list-inside list-disc text-sm text-label-secondary">
                  {form.featuresText
                    .split("\n")
                    .filter(Boolean)
                    .slice(0, 4)
                    .map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                </ul>
              ) : null}
            </div>
          </article>
        </div>
      </div>
      </>
      ) : null}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {saving ? <p className="text-xs text-label-secondary">Online-Buchung wird gespeichert…</p> : null}
      </div>
    </section>
  );
}
